import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
  Avatar,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  SubdirectoryArrowRight as SubIcon,
  Close as CloseIcon,
  ListAlt as ListAltIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import {
  getChecklistByID,
  getChecklistItemsNested,
  updateChecklist,
} from "../../../api/services/settings";
import { QK } from "../../../base/qk";
import CustomInput from "../../FormFields/CustomInput";

const EditChecklistModal = ({
  show,
  handleClose,
  selectedChecklist,
  refetch,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState([]);

  const { isLoading: isInfoLoading } = useQuery(
    [QK.checklist, selectedChecklist],
    () => getChecklistByID(selectedChecklist),
    {
      enabled: !!selectedChecklist && show,
      onSuccess: (data) => setName(data.name),
    }
  );

  const { isLoading: isItemsLoading } = useQuery(
    [QK.checklistItems, selectedChecklist],
    () => getChecklistItemsNested(selectedChecklist),
    {
      enabled: !!selectedChecklist && show,
      onSuccess: (data) => setInstructions(data || []),
    }
  );

  const editMutation = useMutation(
    (payload) => updateChecklist(selectedChecklist, payload),
    {
      onSuccess: () => {
        enqueueSnackbar("Checklist updated successfully", { variant: "success" });
        queryClient.invalidateQueries([QK.checklist]);
        refetch();
        handleClose();
      },
      onError: () => enqueueSnackbar("Update failed", { variant: "error" }),
    }
  );

  const handleInstructionChange = (index, value) => {
    const updated = [...instructions];
    updated[index].text = value;
    setInstructions(updated);
  };

  const handleRemoveInstruction = (index) => {
    setInstructions(instructions.filter((_, i) => i !== index));
  };

  const handleAddInstruction = () => {
    setInstructions([...instructions, { text: "", is_required: true, sub_instructions: [] }]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: name,
      instructions: instructions.filter(inst => inst.text.trim() !== ""),
    };
    editMutation.mutate(payload);
  };

  const isLoading = isInfoLoading || isItemsLoading;

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}
            >
              <ListAltIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Edit Checklist
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Update checklist details and instructions
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2.5 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <CustomInput
                label="Category Name"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />

              <Divider>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  INSTRUCTIONS
                </Typography>
              </Divider>

              {instructions.map((inst, index) => (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #E2E8F0",
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#F8FAFC",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <CustomInput
                      fullWidth
                      label={`Instruction ${index + 1}`}
                      value={inst.text}
                      onChange={(e) => handleInstructionChange(index, e.target.value)}
                    />
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveInstruction(index)}
                      sx={{ mt: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  {inst.sub_instructions?.length > 0 && (
                    <Box sx={{ mt: 1, ml: 2, display: "flex", alignItems: "center", gap: 1 }}>
                      <SubIcon fontSize="small" color="disabled" />
                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        {inst.sub_instructions.length} Nested sub-instructions included
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}

              <Button
                startIcon={<AddIcon />}
                onClick={handleAddInstruction}
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#2469F0",
                  "&:hover": {
                    bgcolor: alpha("#2469F0", 0.04),
                  },
                }}
              >
                Add Instruction
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={editMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#2469F0",
              px: 4,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {editMutation.isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditChecklistModal;